import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, InfoBox, Input, Label } from '@biznomi/ui-react'
import { api } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/store'

/**
 * 초기·임시 비밀번호 변경.
 *
 * 게이트가 mustChangePassword 인 사람을 여기로 보낸다. 화면이 없으면 그 사람은
 * 404 에 갇혀 아무것도 못 한다 — 관리자가 임시 비번을 발급한 직원 전원이 그렇다.
 *
 * ⚠️ 서버는 비번을 바꾸면서 본인의 refresh token 을 전부 무효화한다
 *    (auth.service.ts changePassword — IAM 표준). access token(1h)만 살아 있어
 *    그냥 진행시키면 한 시간 뒤 이유 없이 튕긴다. 그래서 바꾼 즉시 로그아웃하고
 *    새 비번으로 다시 로그인하게 한다. 지금 한 번 귀찮은 게, 나중에 영문 모르고
 *    튕기는 것보다 낫다.
 */

const MIN_LENGTH = 8

export default function ChangePassword() {
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const clearAuth = useAuth((s) => s.clearAuth)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // 서버 규칙은 8자 이상 하나뿐이다 (auth.dto.ts ChangePasswordSchema).
  // 화면에서 대문자·특수문자를 더 요구하면 서버는 통과시키는데 화면만 막는 꼴이 된다.
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const mismatch = confirm.length > 0 && next !== confirm
  const sameAsCurrent = next.length > 0 && next === current
  const canSubmit =
    current.length > 0 && next.length >= MIN_LENGTH && next === confirm && !sameAsCurrent && !busy

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError('')

    const res = await api<{ message: string }>('/api/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword: current, newPassword: next }),
    })

    if (!res.success) {
      setBusy(false)
      setError(res.error ?? '비밀번호를 변경하지 못했습니다')
      return
    }

    // 세션이 서버에서 이미 끊겼다. 로컬도 같이 비우고 로그인으로 보낸다
    clearAuth()
    navigate('/login', { replace: true, state: { notice: '새 비밀번호로 다시 로그인해 주세요' } })
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-6">
      <h1 className="text-xl font-bold">비밀번호를 바꿔 주세요</h1>
      <p className="mt-2 text-sm text-slate-500">
        {user?.name ? `${user.name}님, ` : ''}처음 받은 비밀번호는 관리자도 알고 있습니다. 본인만 아는
        비밀번호로 바꾼 뒤 시작할 수 있습니다.
      </p>

      <form className="mt-6 space-y-4" onSubmit={submit}>
        <div className="space-y-1.5">
          <Label htmlFor="current">현재 비밀번호</Label>
          <Input
            id="current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="next">새 비밀번호</Label>
          <Input
            id="next"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            aria-invalid={tooShort || sameAsCurrent}
            aria-describedby="next-hint"
          />
          <p id="next-hint" className="text-xs text-slate-400">
            {sameAsCurrent
              ? '현재 비밀번호와 다르게 정해 주세요'
              : tooShort
                ? `${MIN_LENGTH}자 이상이어야 합니다`
                : `${MIN_LENGTH}자 이상`}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm">새 비밀번호 확인</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={mismatch}
            aria-describedby="confirm-hint"
          />
          {mismatch && (
            <p id="confirm-hint" className="text-xs text-[var(--color-danger)]">
              두 번 입력한 비밀번호가 다릅니다
            </p>
          )}
        </div>

        {error && <InfoBox tone="danger">{error}</InfoBox>}

        <Button type="submit" className="w-full" disabled={!canSubmit} loading={busy}>
          비밀번호 변경
        </Button>
      </form>

      <p className="mt-4 text-xs leading-relaxed text-slate-400">
        {/*
          🔴 「다른 기기의」라고만 적혀 있었다 (2026-09-12 리허설). 실제로는 **지금 쓰는
             이 기기도** 풀려서 로그인 화면으로 돌아간다. 관리자와 직원 둘 다 그랬다.
             「방금 바꿨는데 또 로그인?」으로 읽히는 자리라 먼저 말해 둔다.
        */}
        바꾸고 나면 이 기기를 포함해 모든 기기의 로그인이 풀립니다. 새 비밀번호로 다시 로그인해 주세요.
      </p>
    </main>
  )
}
